import { useState } from 'react';
import { useSnackbar } from '@/hooks/global-tools';
import { AiChatActionButton } from '@/components/@IntranetLibrary/AiChatView';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { sendPostMessage, PostMessageActionType } from '@/utils/postMessage';
import { stripMarkdown, createTalkFlowDocument } from '../utils';

type AnswerActionsProps = {
  content: string;
  question?: string;
  fileName?: string;
};

// 이 액션에서만 쓰는 20*20 아이콘. 예전엔 public/icons 의 SVG(viewBox 36 안에
// 18px 글리프가 박혀 20px 로 렌더하면 작게 보이던 에셋)를 import 했으나,
// 여기 전용이므로 올바른 20*20 벡터로 인라인한다. 색은 버튼의 color(--aichat-text)를 상속.
const CopyIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g
      transform="translate(3.8245 2.6256)"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M0.5 14.2476H8.67076C8.83745 14.2476 8.99732 14.1814 9.11519 14.0635C9.23306 13.9456 9.29928 13.7858 9.29928 13.6191V5.4482L6.7852 2.93408H1.12852C0.961826 2.93408 0.801959 3.0003 0.684088 3.11817C0.566218 3.23605 0.5 3.39591 0.5 3.56261V14.2476Z" />
      <path d="M9.93313 11.8135H11.2218C11.3885 11.8135 11.5483 11.7473 11.6662 11.6294C11.7841 11.5116 11.8503 11.3517 11.8503 11.185V3.01412L9.33622 0.5H3.67955C3.51285 0.5 3.35298 0.56622 3.23511 0.684092C3.11724 0.801964 3.05103 0.961833 3.05103 1.12853V2.43546" />
    </g>
  </svg>
);

const ExportIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      transform="translate(2.5002 3.9758)"
      d="M14.5 5.44585L7.03341 0.500001L7.03341 3.29997C0.5 4.69996 0.5 11.2332 0.5 11.2332C0.5 11.2332 3.29997 7.49993 7.03341 7.96659L7.03341 10.8599L14.5 5.44585Z"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 파일 뷰어 AI 답변 하단 액션 — 복사하기 / 문서로 내보내기.
 * 버튼/툴팁은 회의록과 동일한 공유 컴포넌트(AiChatActionButton)를 사용하고,
 * 아이콘·문구·동작만 주입한다. 내보내기의 async/disabled 상태는 자체 보유.
 */
const AnswerActions = ({ content, question, fileName }: AnswerActionsProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const toast = useSnackbar();
  const { isMobilePath, isRn } = usePlatform();

  const handleCopy = async () => {
    const plainText = stripMarkdown(content);
    await navigator.clipboard.writeText(plainText);
    toast.showSuccess({ message: '복사가 완료되었습니다.' });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await createTalkFlowDocument(
        content,
        fileName ?? '파일',
        question ?? '질문',
      );
      const documentId = response?.data?.talkFlowDocumentId;
      if (documentId) {
        if (isRn) {
          // 모바일 RN 앱: 새 탭 이동 대신 네이티브 문서 탭으로 전환한다.
          toast.showSuccess({
            message: '문서가 작성되었습니다.',
            action: {
              text: '보러가기 →',
              onClick: () =>
                sendPostMessage(PostMessageActionType.MOVE_TAB, {
                  tabName: 'document',
                  docId: documentId,
                }),
            },
          });
        } else {
          // PC 웹은 /talkflow, 모바일 웹은 /m/talkflow 로 이동해
          // 회의록 화면의 모바일 분기(usePlatform)가 정상 동작하도록 한다.
          toast.showSuccess({
            message: '문서가 작성되었습니다.',
            link: `${isMobilePath ? '/m' : ''}/talkflow/${documentId}`,
          });
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        '문서 생성에 실패했습니다.';
      toast.showError({ message: errorMessage });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <AiChatActionButton tooltip="복사하기" onClick={handleCopy}>
        <CopyIcon />
      </AiChatActionButton>
      <AiChatActionButton
        tooltip="문서로 내보내기"
        onClick={handleExport}
        disabled={isExporting}
      >
        <ExportIcon />
      </AiChatActionButton>
    </>
  );
};

export default AnswerActions;
